import type { DynamicLineItemEntry, TieredDynamicLineItemMember } from '@vy/dynamic-invoice-builder';
import { notDefined } from '@vy/null';
import { formatPrice, formatUnitPrice } from '@vy/services';
import type { Currency, UsagePricingModel } from '@vy/types';
import { MAX_TO } from '@vy/types';
import { formatNumber, vyDate } from '@vy/utils';
import { INFINITY_SYMBOL } from '../../../../../assets/components/design/symbols/infinity-symbol';
import type { UsageLineData } from '../types';

export const isTieredMember = (member: unknown): member is TieredDynamicLineItemMember => {
  return typeof member === 'object' && member !== null && 'isRevShare' in member;
};

const PERIOD_LABEL_FORMAT = 'MMM D, YY';

export const formatPeriodLabel = (fromTimestamp: number, toTimestamp: number): string => {
  const fromStr = vyDate(fromTimestamp).format(PERIOD_LABEL_FORMAT);
  const toStr = vyDate(toTimestamp).format(PERIOD_LABEL_FORMAT);

  return `${fromStr}-${toStr}`;
};

type FormatType = 'number' | 'currency' | 'price';

const formatNumberValue = (value: number): string => {
  if (value === MAX_TO) {
    return INFINITY_SYMBOL;
  }

  return formatNumber(value);
};

const formatValue = (
  value: number | null | undefined,
  formatType: FormatType,
  currency: Currency,
  isRevShare = false,
): string => {
  if (notDefined(value)) {
    return '-';
  }

  switch (formatType) {
    case 'number':
      return formatNumberValue(value);
    case 'currency':
      return formatPrice(value, currency);
    case 'price':
      return isRevShare ? `${formatNumberValue(value)}%` : formatUnitPrice(value, currency);
  }
};

export const formatEntryToTableRow = <T extends UsagePricingModel>(
  entry: DynamicLineItemEntry<T>,
  _pricingModel: T,
  currency: Currency,
  isRevShare = false,
  showSeatDays = false,
): UsageLineData => {
  const e = entry as unknown as Record<string, number | null | undefined>;

  const pricePerUnit = e.unitPrice ?? e.price;
  const periodQtyValue = showSeatDays && pricePerUnit === 0 ? null : e.periodQty;

  return {
    from: formatValue(e.fromUnits, 'number', currency),
    to: formatValue(e.toUnits, 'number', currency),
    unitPrice: formatValue(pricePerUnit, 'price', currency, isRevShare),
    bulkSize: formatValue(e.bulkSize, 'number', currency),
    flatRate: formatValue(e.flatRate, 'currency', currency),
    ytdQty: formatValue(e.ytdQty, 'number', currency),
    periodQty: formatValue(periodQtyValue, 'number', currency),
    billableQty: formatValue(e.billableQty, 'number', currency),
    amount: formatValue(e.amount, 'currency', currency),
  };
};
